import { css } from '@emotion/core'
import { Checkbox, Row, Separator, H3, Paragraph } from '@fs/zion-ui'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'
import { dmcRed } from '../../../util/CssUtil'
import usePersonOrdinances from '../../../hooks/usePersonOrdinances'
import useTempleDirectory from '../../../hooks/useTempleDirectory'
import { MrnLink } from '../../app/Links'
import { PermissionsContext } from '../../app/PermissionsContext'
import { PersonContext } from '../PersonContext'
import FilterOrdinances from './FilterOrdinances'
import CreateCtrButton from './createCtr/CreateCtrButton'
import DeleteOsrButton from './deleteOsr/DeleteOsrButton'
import InvalidateOrdinanceCacheButton from './invalidateOrdinanceCache/InvalidateOrdinanceCacheButton'
import PrintCardsButton from './printCards/PrintCardsButton'
import RepeatedOwsIdsButton from './repeatedOwsIds/RepeatedOwsIdsButton'
import { getOrdinancesFromOrdinanceStatus } from '../../../services/ordinanceService'
import OrdinanceTable from './OrdinanceTable'
import { headerCss } from '../../app/Title'
import useNotifications from '../../../hooks/useNotifications'

const magentaLabel = css`
  label {
    color: ${dmcRed};
  }
`

function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

export default function PersonOrdinances() {
  const { currentPerson } = useContext(PersonContext)
  const [ordinanceType, setOrdinanceType] = useState('all')
  const personId = currentPerson.id
  const { isAuthorized, isRole } = useContext(PermissionsContext)
  const [showInvalid, setShowInvalid] = useState(false)
  const [showEffectiveOnly, setShowEffectiveOnly] = useState(false)
  const { t } = useTranslation()
  const { error } = useNotifications()
  const { ordinanceData, refetchOrdinances, setOrdinanceData } = usePersonOrdinances(personId)
  const { temples } = useTempleDirectory()
  const calculatingCount = useRef(0)
  const isMounted = useRef(true)
  const RECALCULATION_MAX_COUNT = 5

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])
  let bornInTheCovenant = false
  let notAccountable = false
  let ordinanceApprovalRequired = false
  let mrns = null

  function updateOrdinancesAfterFetch(response) {
    setOrdinanceData((prevState) => ({
      ...prevState,
      ordinances: [...response.data, ...prevState.ordinances.filter((ord) => ord.status !== 'CALCULATING')],
    }))
  }

  useEffect(() => {
    function handleRecalculation() {
      getOrdinancesFromOrdinanceStatus(personId).then((response) => {
        updateOrdinancesAfterFetch(response)
      })
    }
    const hasCalculating = ordinanceData?.ordinances?.some((ord) => ord.status === 'CALCULATING')
    if (hasCalculating && calculatingCount.current < RECALCULATION_MAX_COUNT) {
      calculatingCount.current += 1
      delay(500 * calculatingCount.current).then(() => {
        if (isMounted.current) handleRecalculation()
      })
    } else if (calculatingCount.current === RECALCULATION_MAX_COUNT) {
      error(
        t(
          'person.ordinances.errors.recalculationNotCompleted',
          'Recalculation has not yet completed, please check again later'
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, ordinanceData?.ordinances, personId, setOrdinanceData, t])

  if (ordinanceData !== undefined) {
    bornInTheCovenant = new Map(Object.entries(ordinanceData.bornInTheCovenant))
    notAccountable = new Map(Object.entries(ordinanceData.notAccountable))
    ordinanceApprovalRequired = new Map(Object.entries(ordinanceData.ordinanceApprovalRequired))
    mrns = currentPerson?.metadata?.items
      ?.find((item) => item.field === 'mrn' || item.label === 'MRN')
      ?.values?.map((mrnValue) => mrnValue.value)
  }

  if (!ordinanceData || !ordinanceData.ordinances) {
    return null
  }
  const effectiveOrdinances = ordinanceData.ordinances.filter((ord) => ord.effective)
  if (ordinanceData.ordinances.length === 0) {
    return (
      <>
        {(isAuthorized('tss_CreateCtrPermission') || isAuthorized('ViewTempleUIPermission')) && (
          <>
            <Row>
              {isAuthorized('tss_CreateCtrPermission') && (
                <CreateCtrButton currentPerson={currentPerson} refetchOrdinances={refetchOrdinances} />
              )}
              {isAuthorized('ViewTempleUIPermission') && (
                <PrintCardsButton effectiveOrdinances={effectiveOrdinances} ordinanceData={ordinanceData} />
              )}
            </Row>
            <Separator size="xs" />
          </>
        )}
        <div>{t('person.ordinances.noOrdinancesFound', 'No ordinances found')}</div>
      </>
    )
  }
  const atLeastOneEffectiveOrdinance = effectiveOrdinances.length > 0
  let atLeastOneInvalidOrdinance = false

  const getFilteredOrdinances = (asParent) =>
    ordinanceData.ordinances.filter((ord) => {
      const ordIsInvalid = ord.status === 'INVALID'
      if (ordIsInvalid) {
        atLeastOneInvalidOrdinance = true
        if (!showInvalid) {
          return false
        }
      }
      if (!ord.effective && showEffectiveOnly) {
        return false
      }
      if (ordinanceType === 'all') {
        return !!ord.asParent === asParent
      }
      return !!ord.asParent === asParent && ord.type === ordinanceType
    })

  const ordinancesAsSelf = getFilteredOrdinances(false)
  const ordinancesAsParent = getFilteredOrdinances(true)

  const shouldDisplayCheckboxes = () => {
    return (
      Array.from(bornInTheCovenant.values()).includes(true) ||
      Array.from(notAccountable.values()).includes(true) ||
      Array.from(ordinanceApprovalRequired.values()).includes(true)
    )
  }

  const getCheckBoxes = (mrn) => {
    return (
      <React.Fragment key={v4()}>
        {shouldDisplayCheckboxes() && (
          <Row>
            {mrns && mrns.length > 1 && (
              <Paragraph size="sm">
                <MrnLink mrn={mrn} mrnText={`mrn.${mrn}:`} newTab />
              </Paragraph>
            )}
            {bornInTheCovenant?.get(mrn) && (
              <Checkbox
                name="bic"
                label={t('ordinances.flags.bornInTheCovenant', 'Born in the Covenant')}
                disabled
                onChange={() => null}
                checked
              />
            )}
            {notAccountable?.get(mrn) && (
              <Checkbox
                name="notAccountable"
                label={t('ordinances.flags.notAccountable', 'Not Accountable')}
                disabled
                checked
                onChange={() => null}
              />
            )}
            {ordinanceApprovalRequired?.get(mrn) && (
              <Checkbox
                name="oar"
                label={t('accessControl.ordApprovalRequired', 'Ordinance Approval Required')}
                disabled
                checked
                onChange={() => null}
              />
            )}
          </Row>
        )}
      </React.Fragment>
    )
  }
  return (
    <div>
      <Row wrap>
        {isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && (
          <InvalidateOrdinanceCacheButton
            refetchOrdinances={() => {
              calculatingCount.current = 0
              refetchOrdinances()
            }}
          />
        )}
        {isAuthorized('tss_CreateCtrPermission') && (
          <CreateCtrButton currentPerson={currentPerson} refetchOrdinances={refetchOrdinances} />
        )}
        {isAuthorized('ViewTempleUIPermission') && (
          <PrintCardsButton effectiveOrdinances={effectiveOrdinances} ordinanceData={ordinanceData} />
        )}
        {isAuthorized('ModifyOrdinanceSourceReferencePermission') && <DeleteOsrButton />}
        <span growX />
        {ordinanceData.repeatedOwsIdsAndRoles && Object.keys(ordinanceData.repeatedOwsIdsAndRoles).length > 0 && (
          <RepeatedOwsIdsButton owsIdsAndRoles={ordinanceData.repeatedOwsIdsAndRoles} />
        )}
      </Row>
      <Row>
        <FilterOrdinances currentOrdinanceType={ordinanceType} setOrdinanceType={setOrdinanceType} />
        {atLeastOneEffectiveOrdinance && (
          <Checkbox
            data-testid="showEffectiveOnlyLabel"
            name="check1"
            label={t('ordinances.showEffectiveOnly', 'Show Effective Only')}
            checked={showEffectiveOnly}
            onChange={(event) => setShowEffectiveOnly(event.target.checked)}
            disableripple="true"
          />
        )}
        {atLeastOneInvalidOrdinance && (
          <span css={magentaLabel}>
            <Checkbox
              name="check2"
              label={t('ordinances.showInvalid', 'Show Invalid')}
              checked={showInvalid}
              onChange={(event) => setShowInvalid(event.target.checked)}
              disableripple="true"
            />
          </span>
        )}
        {mrns && mrns.length === 1 && getCheckBoxes(mrns[0])}
      </Row>
      {mrns && mrns.length > 1 && mrns.map((mrn) => getCheckBoxes(mrn))}
      {ordinancesAsSelf.length > 0 && (
        <>
          <H3>
            <span css={headerCss}>{t('ordinances.label', 'Ordinances')}</span>
          </H3>
          {temples && (
            <OrdinanceTable
              ordinances={ordinancesAsSelf}
              data-testid="ordinancesAsSelfTable"
              temples={temples}
              mrns={mrns}
              recalculationNotCompleted={calculatingCount.current === RECALCULATION_MAX_COUNT}
            />
          )}
        </>
      )}
      <br />
      {ordinancesAsParent.length > 0 && (
        <>
          <H3>
            <span css={headerCss}>{t('ordinances.asParent', 'Ordinances as Parent')}</span>
          </H3>
          {temples && (
            <OrdinanceTable
              ordinances={ordinancesAsParent}
              data-testid="ordinancesAsParentTable"
              temples={temples}
              mrns={mrns}
            />
          )}
        </>
      )}
    </div>
  )
}
